<template>
  <section
    class="relative flex flex-col gap-2"
    :class="[!!slots.default ? 'pr-10' : '']"
    :aria-label="$t('chat.forwardMessageIsReady')"
  >
    <div
      data-test="forward-message-clickable-area"
      class="px-2 pt-2"
      :class="canGoToForwardedMessageRoom ? 'cursor-pointer' : ''"
      @click="onClick"
    >
      <p class="truncate text-label-r-12-18 text-surf-brand-basedarkblue-def">
        {{ $t('chat.forwardedFrom') }}
      </p>
      <div class="mt-0.5 flex min-w-0 items-center gap-1.5">
        <contact-avatar
          class="shrink-0"
          :contact="forwardingAuthorContact"
          :contact-type="forwardingAuthorContact?.type"
          :name="forwardingAuthorName"
          :size="24"
        />
        <p class="truncate text-label-m-12-18 text-surf-brand-basedarkblue-def">
          {{ forwardingAuthorName }}
        </p>
      </div>
    </div>

    <div
      v-if="file && forwarding.forwardingFiles !== undefined"
      class="w-full min-w-0"
    >
      <file-list
        :files="forwarding.forwardingFiles"
        :type="'IncomingMessage'"
        :event="event"
        full-bleed
      />
    </div>

    <p
      v-if="textToShow"
      ref="textElement"
      class="
        wrap-break-word whitespace-pre-wrap px-2 text-body-r-15-22 text-fg-base-fg3
        [&_a]:text-comp-link-neut-def
        [&_b[data-contact-uri]]:cursor-pointer [&_b[data-contact-uri]]:font-normal [&_b[data-contact-uri]]:text-surf-brand-basedarkblue-def
      "
      @click.stop
      v-html="textToShow"
    />
    <i
      v-else-if="!files.length"
      class="px-3"
    >
      {{ $t('chat.messageRemoved') }}
    </i>

    <div
      v-if="canExpandText && isTextTruncated"
      class="mx-3 flex cursor-pointer items-center gap-1 pr-1"
      @click="toggleExpand"
    >
      <p class="text-body-r-14-20 text-comp-link-neut-def hover:underline">
        {{ isExpanded ? $t('common.collapse') : $t('common.expand') }}
      </p>
      <wui-icon
        :name="isExpanded ? 'chevronUpSmM' : 'chevronDownSmM'"
        class="h-5! w-5! text-comp-link-neut-def"
      />
    </div>

    <slot :color="color" />
  </section>
</template>

<script setup lang="ts">
import { WuiIcon } from '@wui/common-library'
import { HistoryMessageItem, IMForwarding, MessageIncomingEvent, MessageOutgoingEvent } from '@wui/im'
import { computed, useSlots, onMounted, watch, nextTick } from 'vue'

import { FileList } from '@/entities/chat-event-file'
import { ContactAvatar } from '@/entities/contact-avatar'
import { useStoreRooms } from '@/entities/room'

import { useTextExpand, LINES_COUNT_TO_CLAMP_MESSAGE } from '@/shared/composables/use-text-expand'
import { useUserColor } from '@/shared/composables/user-color'
import { isImageFile } from '@/shared/utils/files'
import { optionalPush } from '@/shared/utils/router'
import { fromPServedUserIfRoom } from '@/shared/utils/uri'
import { getChatRoute } from '@/shared/utils/url'

import {
  resolveForwardingAuthorContact,
  resolveForwardingAuthorName,
} from '../lib/get-forwarding-author-name'

/* eslint-disable vue/require-default-prop -- canExpandText, event, id omitted from defaults to satisfy exactOptionalPropertyTypes */
const props = withDefaults(defineProps<{
  forwarding: IMForwarding,
  allowToGoToForwardedMessageRoom?: boolean
  canExpandText?: boolean
  event?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined
  id?: number
}>(), {
  allowToGoToForwardedMessageRoom: true,
})
/* eslint-enable vue/require-default-prop */

const { getColorByName } = useUserColor()
const slots = useSlots()
const { findByPServed } = useStoreRooms()

const forwardingAuthorName = computed(() => resolveForwardingAuthorName(props.forwarding))
const forwardingAuthorContact = computed(() => resolveForwardingAuthorContact(props.forwarding))
const textToShow = computed(() => props.forwarding.forwardingText || '')
const files = computed(() => props.forwarding.forwardingFiles ?? [])

const {
  isExpanded,
  textElement,
  isTextTruncated,
  initTextExpanding,
  toggleExpand,
} = useTextExpand(textToShow, LINES_COUNT_TO_CLAMP_MESSAGE)

const color = computed(() => {
  if (forwardingAuthorName.value) {
    return getColorByName(forwardingAuthorName.value, false)
  }
  return 'transparent'
})

const canGoToForwardedMessageRoom = computed(() => {
  const { forwardingChatPServed } = props.forwarding
  return props.allowToGoToForwardedMessageRoom && (!!forwardingChatPServed && !!findByPServed(forwardingChatPServed))
})

const onClick = () => {
  const { forwardingChatPServed, forwardingEventId } = props.forwarding
  if (!canGoToForwardedMessageRoom.value || !forwardingChatPServed) {
    return
  }
  const roomId = fromPServedUserIfRoom(forwardingChatPServed)
  optionalPush(getChatRoute(roomId, { hash: `${forwardingEventId}` }))
}

const file = computed(() => {
  const image = files.value.find((file) => isImageFile(file.filename))
  return image ?? files.value.find((file) => !isImageFile(file.filename))
})

onMounted(() => {
  if (props.canExpandText) {
    initTextExpanding()
  }
})

watch(() => props.id, async (n, o) => {
  if (n !== o && n) {
    await nextTick()

    initTextExpanding()
  }
})
</script>
